import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { useTranslation } from "react-i18next";
import {
  addProduct,
  getDesigners,
  getProducts,
} from "../../api/Design/productApi";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import { getModelosTipo } from "../../api/Design/modelosTipoApi";
import { getSubgrupos } from "../../api/Subgrupos/subgrupoApi";
import { getMarcas } from "../../api/Marcas/marcaApi";

const emptyNewProduct = () => ({
  tipo_prenda: "",
  ID_Temporada: "",
  ID_SUBGRUPO: "",
  diseñador: "",
  nombre: "",
  ID_MARCA: "",
});

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const pop = keyframes`
  0%, 10% { opacity: 0.25; transform: translateY(6px) scale(0.9); }
  30%, 65% { opacity: 1; transform: translateY(0) scale(1); }
  100% { opacity: 0.35; transform: translateY(3px) scale(0.95); }
`;

const slideIn = keyframes`
  0%, 15% { opacity: 0.2; transform: translateX(-10px) scale(0.92); }
  40%, 70% { opacity: 1; transform: translateX(0) scale(1); }
  100% { opacity: 0.35; transform: translateX(4px) scale(0.96); }
`;

const pulseArrow = keyframes`
  0%, 100% { transform: translateX(0); opacity: 0.45; }
  50% { transform: translateX(4px); opacity: 1; }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

function Stage({ children, tint }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: { xs: 72, sm: 68 },
        overflow: "hidden",
        bgcolor: tint,
      }}
    >
      {children}
    </Box>
  );
}

/** Buscar diseño existente → muestra */
function ExistingPreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.14)}>
      <Box
        sx={{
          position: "absolute",
          left: 12,
          top: 14,
          width: 38,
          height: 38,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.2)}`,
          animation: `${pop} 3.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <SearchOutlinedIcon sx={{ fontSize: 20, color: accent }} />
      </Box>
      {[0, 1].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 58 + i * 10,
            top: 16 + i * 8,
            width: 44,
            height: 28,
            borderRadius: 1,
            bgcolor: "#fff",
            border: "1.5px solid",
            borderColor: alpha(accent, 0.35),
            boxShadow: `0 4px 10px ${alpha(accent, 0.14)}`,
            animation: `${slideIn} 3.8s ease-in-out infinite`,
            animationDelay: `${i * 0.15}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          right: 12,
          top: 18,
          width: 36,
          height: 36,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.2),
          border: "1px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <StyleOutlinedIcon sx={{ fontSize: 18, color: accent }} />
      </Box>
    </Stage>
  );
}

/** Campos → producto nuevo → muestra */
function NewProductPreview({ accent }) {
  return (
    <Stage tint={alpha(accent, 0.14)}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: 10,
            top: 12 + i * 16,
            width: 42 - i * 4,
            height: 10,
            borderRadius: 0.75,
            bgcolor: alpha(accent, 0.2 + i * 0.12),
            border: "1px solid",
            borderColor: alpha(accent, 0.35),
            animation: `${pop} 4s ease-in-out infinite`,
            animationDelay: `${i * 0.12}s`,
            ...motionSafe,
          }}
        />
      ))}
      <Box
        sx={{
          position: "absolute",
          left: "46%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          color: accent,
          animation: `${pulseArrow} 1.6s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 22 }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: 12,
          top: 14,
          width: 40,
          height: 40,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.22)}`,
          animation: `${bob} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <AddBoxOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
    </Stage>
  );
}

function IntentCard({
  active,
  preview: Preview,
  title,
  body,
  accent,
  onClick,
  delayMs = 0,
}) {
  const theme = useTheme();
  const muted = theme.palette.grey[400];
  const ink = active ? accent : muted;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        p: 0,
        m: 0,
        width: "100%",
        minWidth: 0,
        textAlign: "left",
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        border: "2.5px solid",
        borderColor: active ? accent : alpha(theme.palette.text.primary, 0.12),
        bgcolor: active
          ? alpha(accent, 0.12)
          : alpha(theme.palette.grey[500], 0.06),
        boxShadow: active ? `0 10px 24px ${alpha(accent, 0.28)}` : "none",
        opacity: active ? 1 : 0.55,
        filter: active ? "none" : "grayscale(0.85)",
        transition:
          "border-color 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease, opacity 0.18s ease, filter 0.18s ease",
        font: "inherit",
        color: "inherit",
        animation: `${rise} 320ms ease both`,
        animationDelay: `${delayMs}ms`,
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
        ...motionSafe,
        "&:active": { transform: "scale(0.985)" },
        "&:hover": {
          opacity: 1,
          filter: "none",
          borderColor: active ? accent : alpha(accent, 0.55),
          bgcolor: alpha(accent, active ? 0.16 : 0.08),
        },
        "&:focus-visible": {
          outline: `2px solid ${accent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Preview accent={ink} />
      <Box
        sx={{
          px: 1.15,
          py: 1.1,
          bgcolor: active
            ? alpha(accent, 0.1)
            : alpha(theme.palette.grey[500], 0.04),
          borderTop: "1px solid",
          borderColor: active
            ? alpha(accent, 0.22)
            : alpha(theme.palette.text.primary, 0.08),
        }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          {active ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 18, color: accent }} />
          ) : null}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              lineHeight: 1.2,
              fontSize: { xs: "0.8rem", sm: "0.78rem" },
              color: ink,
            }}
          >
            {title}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.35,
            lineHeight: 1.35,
            fontSize: { xs: "0.7rem", sm: "0.68rem" },
            color: active
              ? alpha(theme.palette.text.primary, 0.65)
              : alpha(theme.palette.text.primary, 0.4),
          }}
        >
          {body}
        </Typography>
      </Box>
    </Box>
  );
}

export default function MeetingCreateSampleDialog({
  open,
  onClose,
  onProductChosen,
  mediaCount = 0,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [marcas, setMarcas] = useState([]);
  const [temporadas, setTemporadas] = useState([]);
  const [modelosTipo, setModelosTipo] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [newProduct, setNewProduct] = useState(emptyNewProduct);

  const existingAccent = theme.palette.primary.main || "#0f766e";
  const newAccent = "#9a3412";

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError("");
    setSelectedProduct(null);
    setNewProduct(emptyNewProduct());
    setTab(0);
    setLoading(true);

    (async () => {
      try {
        const [
          productsData,
          marcasData,
          temporadasData,
          modelosData,
          subgruposData,
          designersData,
        ] = await Promise.all([
          getProducts(),
          getMarcas(),
          getTemporadas(),
          getModelosTipo(),
          getSubgrupos(),
          getDesigners(),
        ]);
        if (cancelled) return;
        setProducts(Array.isArray(productsData) ? productsData : []);
        setMarcas(Array.isArray(marcasData) ? marcasData : []);
        setTemporadas(Array.isArray(temporadasData) ? temporadasData : []);
        setModelosTipo(Array.isArray(modelosData) ? modelosData : []);
        setSubgrupos(Array.isArray(subgruposData) ? subgruposData : []);
        setDesigners(Array.isArray(designersData) ? designersData : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err.message || "Error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  // Auto-nombre tipo ProductTree
  useEffect(() => {
    if (!open || tab !== 1) return;
    if (
      !newProduct.diseñador ||
      !newProduct.ID_Temporada ||
      !newProduct.tipo_prenda
    ) {
      return;
    }
    const designerAbbr =
      designers.find((d) => d.id === newProduct.diseñador)?.abreviatura ||
      "DES";
    const temporadaAbbr =
      temporadas.find((t) => t.ID_TEMPORADA === newProduct.ID_Temporada)
        ?.ABREVIATURA || "TEMP";
    const modelo = modelosTipo.find(
      (m) => m.ID_TIPO === parseInt(newProduct.tipo_prenda, 10),
    );
    const modeloAbbr = modelo?.ABREVIATURA || "MOD";
    const randomDigits = Math.floor(1000 + Math.random() * 9000);
    const generatedName = `${designerAbbr}${temporadaAbbr}${modeloAbbr}${randomDigits}`;
    if (!newProduct.nombre || newProduct.nombre.startsWith("DES")) {
      setNewProduct((prev) => ({ ...prev, nombre: generatedName }));
    }
  }, [
    open,
    tab,
    newProduct.diseñador,
    newProduct.ID_Temporada,
    newProduct.tipo_prenda,
    newProduct.nombre,
    designers,
    temporadas,
    modelosTipo,
  ]);

  const productOptions = useMemo(
    () =>
      (products || []).map((p) => ({
        ...p,
        label: `${p.nombre || "—"} · #${p.id}`,
      })),
    [products],
  );

  const canConfirmExisting = Boolean(selectedProduct?.id);
  const canConfirmNew =
    Boolean(newProduct.ID_MARCA) &&
    Boolean(newProduct.ID_Temporada) &&
    Boolean(newProduct.tipo_prenda) &&
    Boolean(newProduct.ID_SUBGRUPO) &&
    Boolean(newProduct.diseñador) &&
    Boolean(String(newProduct.nombre || "").trim());

  const handleConfirm = async () => {
    setError("");
    setSaving(true);
    try {
      if (tab === 0) {
        if (!selectedProduct?.id) {
          setError(t("Meetings.createSamplePickProduct"));
          return;
        }
        await onProductChosen?.({
          id: selectedProduct.id,
          ID_MARCA: selectedProduct.ID_MARCA,
          nombre: selectedProduct.nombre,
        });
        onClose?.();
        return;
      }

      const designerAbbr =
        designers.find((d) => d.id === newProduct.diseñador)?.abreviatura ||
        "DES";
      const temporadaAbbr =
        temporadas.find((t) => t.ID_TEMPORADA === newProduct.ID_Temporada)
          ?.ABREVIATURA || "TEMP";
      const modelo = modelosTipo.find(
        (m) => m.ID_TIPO === parseInt(newProduct.tipo_prenda, 10),
      );
      const modeloAbbr = modelo?.ABREVIATURA || "MOD";
      const randomDigits = Math.floor(1000 + Math.random() * 9000);
      const generatedName = `${designerAbbr}${temporadaAbbr}${modeloAbbr}${randomDigits}`;
      const nombreFinal =
        !newProduct.nombre || newProduct.nombre.startsWith("DES")
          ? generatedName
          : newProduct.nombre.trim();

      const payload = {
        tipo_prenda: newProduct.tipo_prenda,
        main_fabric: newProduct.ID_SUBGRUPO,
        diseñador: newProduct.diseñador,
        nombre: nombreFinal,
        ID_Temporadas: newProduct.ID_Temporada,
        ID_MARCA: newProduct.ID_MARCA,
      };

      const res = await addProduct(payload);
      const created = res?.producto || res;
      const productId = created?.id;
      if (!productId) {
        throw new Error(t("Meetings.createSampleProductError"));
      }
      await onProductChosen?.({
        id: productId,
        ID_MARCA: created.ID_MARCA ?? newProduct.ID_MARCA,
        nombre: created.nombre || nombreFinal,
      });
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err.message ||
          t("Meetings.createSampleProductError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: (th) => alpha(th.palette.background.default, 0.55),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          maxHeight: "min(92vh, 820px)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          flexShrink: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.12)} 0%, ${alpha(
              "#9a3412",
              0.08,
            )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.16),
                color: "primary.main",
                flexShrink: 0,
              }}
            >
              <StyleOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.createSampleProductTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
              >
                {t("Meetings.createSampleProductHint", { count: mediaCount })}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            disabled={saving}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        {mediaCount > 0 ? (
          <Chip
            size="small"
            icon={
              <CollectionsOutlinedIcon sx={{ fontSize: "16px !important" }} />
            }
            label={t("Meetings.createSampleMediaChip", { count: mediaCount })}
            sx={{
              mt: 1.25,
              height: 26,
              fontWeight: 800,
              borderRadius: 1.25,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "& .MuiChip-icon": { color: "primary.main" },
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          px: 2.5,
          pt: 1.75,
          pb: 1,
          overflow: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 1.15,
            mb: 2,
          }}
        >
          <IntentCard
            active={tab === 0}
            preview={ExistingPreview}
            title={t("Meetings.createSampleTabExisting")}
            body={t("Meetings.createSampleTabExistingHint")}
            accent={existingAccent}
            onClick={() => !saving && setTab(0)}
            delayMs={40}
          />
          <IntentCard
            active={tab === 1}
            preview={NewProductPreview}
            title={t("Meetings.createSampleTabNew")}
            body={t("Meetings.createSampleTabNewHint")}
            accent={newAccent}
            onClick={() => !saving && setTab(1)}
            delayMs={90}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box
            key={`create-sample-tab-${tab}`}
            sx={{
              animation: `${rise} 280ms ease both`,
              ...motionSafe,
            }}
          >
            {tab === 0 ? (
              <Autocomplete
                options={productOptions}
                value={selectedProduct}
                onChange={(_, value) => setSelectedProduct(value)}
                getOptionLabel={(opt) =>
                  opt?.label || opt?.nombre || String(opt?.id || "")
                }
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Meetings.createSampleSearchProduct")}
                    placeholder={t("Meetings.createSampleSearchPlaceholder")}
                    sx={fieldSx}
                  />
                )}
              />
            ) : (
              <Stack spacing={1.6}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("Meetings.productBrand")}</InputLabel>
                  <Select
                    label={t("Meetings.productBrand")}
                    value={newProduct.ID_MARCA}
                    onChange={(e) =>
                      setNewProduct((prev) => ({
                        ...prev,
                        ID_MARCA: e.target.value,
                      }))
                    }
                  >
                    {marcas.map((m) => (
                      <MenuItem key={m.ID_MARCA} value={m.ID_MARCA}>
                        {m.MARCA}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("Meetings.productSeason")}</InputLabel>
                  <Select
                    label={t("Meetings.productSeason")}
                    value={newProduct.ID_Temporada}
                    onChange={(e) =>
                      setNewProduct((prev) => ({
                        ...prev,
                        ID_Temporada: e.target.value,
                      }))
                    }
                  >
                    {temporadas.map((temp) => (
                      <MenuItem
                        key={temp.ID_TEMPORADA}
                        value={temp.ID_TEMPORADA}
                      >
                        {temp.TEMPORADA}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("Meetings.productGarmentType")}</InputLabel>
                  <Select
                    label={t("Meetings.productGarmentType")}
                    value={newProduct.tipo_prenda}
                    onChange={(e) =>
                      setNewProduct((prev) => ({
                        ...prev,
                        tipo_prenda: e.target.value,
                      }))
                    }
                  >
                    {modelosTipo.map((m) => (
                      <MenuItem key={m.ID_TIPO} value={m.ID_TIPO}>
                        {m.TIPO}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("Meetings.productMainFabric")}</InputLabel>
                  <Select
                    label={t("Meetings.productMainFabric")}
                    value={newProduct.ID_SUBGRUPO}
                    onChange={(e) =>
                      setNewProduct((prev) => ({
                        ...prev,
                        ID_SUBGRUPO: e.target.value,
                      }))
                    }
                  >
                    {subgrupos.map((s) => (
                      <MenuItem key={s.ID_SUBGRUPO} value={s.ID_SUBGRUPO}>
                        {s.SUBGRUPO}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>{t("Meetings.productDesigner")}</InputLabel>
                  <Select
                    label={t("Meetings.productDesigner")}
                    value={newProduct.diseñador}
                    onChange={(e) =>
                      setNewProduct((prev) => ({
                        ...prev,
                        diseñador: e.target.value,
                      }))
                    }
                  >
                    {designers.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.username || d.nombre || d.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  label={t("Meetings.productName")}
                  value={newProduct.nombre}
                  onChange={(e) =>
                    setNewProduct((prev) => ({
                      ...prev,
                      nombre: e.target.value,
                    }))
                  }
                  sx={fieldSx}
                />
              </Stack>
            )}
          </Box>
        )}

        {error ? (
          <Typography
            color="error"
            variant="body2"
            fontWeight={700}
            sx={{ mt: 1.5 }}
          >
            {error}
          </Typography>
        ) : null}
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button onClick={onClose} disabled={saving} sx={{ fontWeight: 700 }}>
          {t("Meetings.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={
            saving ||
            loading ||
            (tab === 0 ? !canConfirmExisting : !canConfirmNew)
          }
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            fontWeight: 800,
            borderRadius: 2,
            px: 2,
            boxShadow: `0 8px 18px ${alpha(
              tab === 0 ? existingAccent : newAccent,
              0.28,
            )}`,
            bgcolor: tab === 0 ? existingAccent : newAccent,
            "&:hover": {
              bgcolor: tab === 0 ? existingAccent : newAccent,
              filter: "brightness(0.94)",
            },
          }}
        >
          {t("Meetings.createSampleContinue")}
        </Button>
      </Stack>
    </Dialog>
  );
}
